import { GoogleSignin } from '@react-native-google-signin/google-signin';
import { useFocusEffect } from '@react-navigation/native';
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { useSelector } from 'react-redux';

import actions from '../actions';
import { Platform } from '../common/react';
import { backupInfoProcessing } from '../screens/Services/Backup/Backup';
import { setBackupError } from '../screens/Services/Backup/common/errors';
import {
    getDriveAccessToken,
    getGoogleDriveBackupInfo
} from '../screens/Services/Backup/google/googleDrive';
import {
    getLastBackups,
    icloudFileListShort
} from '../screens/Services/Backup/icloud/icloud';
import { getUserInfo } from '../selectors';
import { getFormValues } from '../selectors/forms';
import {
    dbGettr,
    prefGettr
} from '../selectors/utils';
import { consoleLog } from '../utils/log';
import { useNetworkStatus } from './useNetworkStatus';

const toLog = (message, data) => {
    if (data) {
        // eslint-disable-next-line no-console
        console.log(message, data);
    } else {
        // eslint-disable-next-line no-console
        console.log(`[useBackup]`, message);
    }
};

// eslint-disable-next-line max-statements
export const useBackup = ({ useInteractive = true } = {}) => {
    const {
        hashKey = '',
        userId,
        userId: profileId,
    } = useSelector(getUserInfo);

    const [ useGoogleBackup, setUseGoogleBackup ] = useState(Platform.OS === 'android');
    const [ useGoogleRestore, setUseGoogleRestore ] = useState(Platform.OS === 'android');
    const [ useICloudBackup, setUseICloudBackup ] = useState(Platform.OS === 'ios');
    const [ googleLastBackup, setGoogleLastBackup ] = useState(null);
    const [ iCloudLastBackup, setICloudLastBackup ] = useState(null);

    const [ isBackupProcessing, setIsBackupProcessing ] = useState(false);
    const { backupProcessing = false } = useSelector(getFormValues);
    const [ googleDriveConnected, setGoogleDriveConnected ] = useState(false);
    const [ error, setError ] = useState('');
    const googleDriveIosBackup = useSelector(dbGettr('googleDriveIosBackup'));
    const googleDriveIosRestore = useSelector(dbGettr('googleDriveIosRestore'));
    const [ lastBackupDateGoogle, setLastBackupDateGoogle ] = useState(null);
    const [ lastBackupFileGoogle, setLastBackupFileGoogle ] = useState('');
    const [ lastBackupFileICloud, setLastBackupFileICloud ] = useState('');
    const [ lastBackupDateICloud, setLastBackupDateICloud ] = useState(null);
    const [ iCloudBackupSize, setICloudBackupSize ] = useState(0);
    const [ accessToken, setAccessToken ] = useState(null);
    const connectProcessing = useRef(false);
    const infoProcessing = useRef(false);
    const iCloudInfoProcessing = useRef(false);
    const network = useNetworkStatus();
    const [ googleDriveBackupSize, setGoogleDriveBackupSize ] = useState(0);
    const [ isInfoLoading, setIsInfoLoading ] = useState(false);
    const [ isICloudInfoProcessing, setIsICloudInfoProcessing ] = useState(false);
    const googleInfoReady = useRef(false);
    const googleCloudEnabled = useSelector(prefGettr('googleCloudEnabled', false));
    const iCloudEnabled = useSelector(prefGettr('iCloudEnabled', false));
    const backupVisitAttachments = useSelector(prefGettr('backupVisitAttachments', true)) ?? true;
    const backupMyDocs = useSelector(prefGettr('backupMyDocs', true)) ?? true;
    const backupAttachmentsWifi = useSelector(prefGettr('backupAttachmentsWifi', true));

    const setBackupProcessing = (value) => {
        actions.setFormValue('backupProcessing', value);
    };

    const getGoogleDiskInfo = useCallback(async () => {
        if (infoProcessing.current) {
            return;
        }

        infoProcessing.current = true;
        setIsInfoLoading(true);

        getGoogleDriveBackupInfo(profileId, googleCloudEnabled && useInteractive)
            .then((result) => {
                setGoogleLastBackup(result?.backups?.[0]);
                consoleLog('getGoogleDiskInfo result:', result);
                setLastBackupDateGoogle(result?.lastBackup);
                setLastBackupFileGoogle(result?.lastBackupFile);
                setGoogleDriveBackupSize(result?.backupSize || 0);
                setGoogleDriveConnected(true);
            })
            .catch(() => {
                setGoogleDriveConnected(false);
            })
            .finally(() => {
                infoProcessing.current = false;
                setIsInfoLoading(false);
            });
    }, [ profileId,
        googleCloudEnabled,
        useInteractive ]);

    const getICloudInfo = useCallback(async () => {
        toLog('getICloudInfo called', {
            useICloudBackup,
            iCloudEnabled,
            profileId,
            hashKey,
        });
        if (Platform.OS !== 'ios' || !profileId || !hashKey) {
            setLastBackupDateICloud(null);
            setICloudBackupSize(0);
            toLog('getICloudInfo: iCloud backup not enabled or missing profileId/hashKey');
            return;
        }

        if (iCloudInfoProcessing.current) {
            toLog('getICloudInfo: already processing');
            return;
        }

        iCloudInfoProcessing.current = true;
        setIsICloudInfoProcessing(true);

        try {
            const info = await getLastBackups({ profileId });
            toLog('getICloudInfo: retrieved info', {
                fileName: info?.fileName,
                createdTime: info?.createdTime?.toString?.(),
                size: info?.size,
                attachments: icloudFileListShort(info?.attachments)
                    ?.join(', '),
                info,
            });
            const time = info?.[0]?.file?.birthtimeMs;
            setICloudLastBackup(info?.[0]);
            setLastBackupDateICloud(time ? new Date(time) : null);
            setLastBackupFileICloud(info?.[0]?.file?.name || '');
            setICloudBackupSize(info?.[0]?.size || 0);
        } catch (e) {
            setLastBackupDateICloud(null);
            setICloudBackupSize(0);
            setBackupError(Object.R(e?.message ?? ''));
            toLog('Error getting iCloud info:', e);
        } finally {
            iCloudInfoProcessing.current = false;
            setIsICloudInfoProcessing(false);
        }
    }, [ useICloudBackup,
        iCloudEnabled,
        profileId,
        hashKey ]);

    useEffect(() => {
        if (isBackupProcessing && !backupProcessing) {
            getGoogleDiskInfo()
                .then()
                .catch();
            toLog('ICloud:getICloudInfo:backupProcessing change', { backupProcessing });
            getICloudInfo()
                .then()
                .catch();
        }
        setIsBackupProcessing(backupProcessing);
    }, [ backupProcessing ]);

    const disconnectGoogleDrive = useCallback(async () => {
        if (!googleDriveConnected || connectProcessing.current) {
            return;
        }

        connectProcessing.current = true;

        try {
            await GoogleSignin.signOut();
            setGoogleDriveConnected(false);
            setError('');
        } catch (e) {
            setError(e.message ?? 'Google Drive disconnection error');
            toLog('Error disconnecting from Google Drive:', e);
        } finally {
            googleInfoReady.current = false;
            connectProcessing.current = false;
            setGoogleDriveBackupSize(0);
            setLastBackupDateGoogle(null);
        }
    }, [ googleDriveConnected ]);

    const connectGoogleDrive = useCallback(
        async ({
            onConnect,
            disableInteractive = false,
            onCancel = () => {
            },
        }) => {
            toLog('connectGoogleDrive called', {
                googleDriveConnected,
                connectProcessing: connectProcessing.current,
                disableInteractive,
            });
            if (connectProcessing.current) {
                return;
            }

            connectProcessing.current = true;
            setIsInfoLoading(true);
            getDriveAccessToken(backupInfoProcessing.interactive, onCancel)
                .then((token) => {
                    if (token) {
                        setGoogleDriveConnected(true);
                        setError('');
                        setAccessToken(token);
                        onConnect?.(true);
                        getGoogleDiskInfo();
                    } else {
                        toLog('connectGoogleDrive: error: no token', token);
                        setError('Google Drive connection failed');
                        setAccessToken(null);
                        setIsInfoLoading(false);
                    }
                })
                .catch((e) => {
                    setError(e.message ?? 'Google Drive connection error');
                    toLog('Error connecting to Google Drive:', e);
                    setIsInfoLoading(false);
                })
                .finally(() => {
                    connectProcessing.current = false;
                    googleInfoReady.current = false;
                });
            if (disableInteractive) {
                backupInfoProcessing.interactive = false;
            }
        },
        [ getGoogleDiskInfo, googleDriveConnected ],
    );

    useEffect(() => {
        if (Platform.OS === 'ios') {
            setUseGoogleBackup(googleDriveIosBackup);
            setUseGoogleRestore(googleDriveIosRestore);
        }
    }, [ googleDriveIosRestore, googleDriveIosBackup ]);

    useFocusEffect(
        useCallback(() => {
            if (googleCloudEnabled !== false) {
                getGoogleDiskInfo()
                    .then()
                    .catch();
            }
        }, [ getGoogleDiskInfo, googleCloudEnabled ]),
    );

    useFocusEffect(
        useCallback(() => {
            if (iCloudEnabled && useICloudBackup) {
                getICloudInfo()
                    .then()
                    .catch();
            }
        }, [ getICloudInfo,
            iCloudEnabled,
            useICloudBackup ]),
    );

    return {
        useGoogleBackup,
        useGoogleRestore,
        setUseGoogleBackup,
        useICloudBackup,
        setUseICloudBackup,
        googleDriveConnected,
        error,
        connectGoogleDrive,
        profileId: userId,
        hashKey,
        lastBackupDateGoogle,
        lastBackupDateICloud,
        disconnectGoogleDrive,
        // backupDataString,
        network,
        googleDriveBackupSize,
        iCloudBackupSize,
        accessToken,
        setBackupProcessing,
        backupProcessing,
        isInfoLoading,
        isICloudInfoProcessing,
        isBackupProcessing,
        googleCloudEnabled,
        iCloudEnabled,
        backupMyDocs,
        backupVisitAttachments,
        backupAttachmentsWifi,
        getGoogleDiskInfo,
        getICloudInfo,
        lastBackupFileGoogle,
        lastBackupFileICloud,
        googleLastBackup,
        iCloudLastBackup,
    };
};
